import {
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    EmbeddedViewRef,
    EventEmitter,
    Input,
    OnChanges,
    OnInit,
    Output,
    TemplateRef,
    ViewChild
} from '@angular/core';
import { NestedData } from './nested-data';
import { CdkVirtualForOf, CdkVirtualForOfContext, CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { CdkDragDrop, CdkDropList } from '@angular/cdk/drag-drop';

/**
 * This provides a virtualized table for especially large data sets. It has a different styling from the other tables
 * and is meant to display large nested data structures. To use this, wrap your data using the NestedData model found
 * in this directory. Then, supply the following required properties. You may supply any of the optional properties.
 *
 * REQUIRED
 * @Input data: an array of nested data that will be displayed.
 * @Input columns: the columns that will be on the table. This is a dictionary of string keys to string arrays.
 * 			The key is the identifier of the nested datum. The string array are the columns that display for that
 * 			identifier.
 * @Input cellTemplate: a template that represents the contents of the cells. The template is supplied a context of
 * 			the datum and the column of the cell as {datum: datum, column: column}.
 *
 * 	OPTIONAL
 * 	@Input height: this is the height styling for the viewport. It is a string and will be injected directly to the style.
 * 	@Input width: this is the width styling of the viewport. It is a string and will be injected directly to the style.
 * 	@Input paddingLeftPerLevel: this is the amount of padding there will be at a given level for each level of nesting.
 *  @Input draggableIdentifiers: these are the datum that are draggable.
 *  @Input draggableConnectedLists: this is a list of cdkDropLists that are capable of being dragged into
 *
 * 	OUTPUT
 * 	@Output dragStart: This event fires the nested data when the user starts dragging its cell
 * 	@Output dragEnd: This event fires the nested data when the user ends dragging its cell.
 * 			NOTE: this event is volatile and sometimes doesn't fire correctly. Avoid using it.
 */
@Component({
    selector: 'virtualized-table',
    templateUrl: './virtualized-table.component.html',
    styleUrls: [
        './virtualized-table.component.css',
        '../../../assets/styles/angular-material.scss'
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class VirtualizedTableComponent implements OnInit, OnChanges {

    // Interface Properties
    @Input() data: NestedData[];
    @Input() columns: { [identifier: string]: string[] } = {};
    @Input() draggableIdentifiers: string[] = [];
    @Input() draggableConnectedLists: CdkDropList[];
    @Input() cellTemplate: TemplateRef<any>;
    @Input() height = '300px';
    @Input() width = '100px';
    @Input() paddingLeftPerLevel = 20;
    @Output() fieldDrop = new EventEmitter<CdkDragDrop<any, any>>();
    @Output() dragStart = new EventEmitter<NestedData>();
    @Output() dragEnd = new EventEmitter<NestedData>();

    // Local Properties
    flatData: NestedData[] = [];
    @ViewChild('scrollComponent', { read: ElementRef, static: true }) scrollComponent: ElementRef;
    @ViewChild('virtualizedDropList', { read: CdkDropList, static: true }) virtualizedDropList: CdkDropList;
    @ViewChild(CdkVirtualScrollViewport) viewPort: CdkVirtualScrollViewport;

    // Class Functions
    constructor() { }

    // Event Functions
    ngOnInit() {
        // POST: applying patch to prevent errors
        CdkVirtualForOf.prototype['_updateContext'] = function (this: any) {
            const count = this._data.length;
            let i = this._viewContainerRef.length;
            while (i--) {
                const view = this._viewContainerRef.get(i) as EmbeddedViewRef<CdkVirtualForOfContext<any>>;
                if (!view.destroyed) {
                    view.context.index = this._renderedRange.start + i;
                    view.context.count = count;
                    this._updateComputedContextProperties(view.context);
                    view.detectChanges();
                }
            }
        };
    }

    ngOnChanges() {
        // POST: assembles the data whenever a property changes
        this.assembleData();
    }

    rowClick(event: MouseEvent, row: NestedData) {
        // PRE: a row is clicked
        // POST: expands its children and sets it as selected

        // If this is a header row, don't do anything
        if (row.header) {
            return;
        }

        // Showing children
        row.showChildren = !row.showChildren;

        // Running the datums click funciton
        if (event && row.click) {
            row.click(event, row);
        }

        // Don't do any selection if we're already selected
        if (row.selected) {
            return;
        }

        // Unselecting peers and their children
        this.flatData.forEach(data => {
            data.selected = false;
        });

        // Selecting the row
        row.selected = true;

        // Selecting the parents
        while (row.parent) {
            row.parent.selected = true;
            row = row.parent;
        }
    }

    rowKeydown(event: KeyboardEvent, row: NestedData) {
        // PRE: a row is focused while a key is pressed
        // POST: behaves identically to a click if enter is pressed
        if (event.key == 'Enter') {
            this.rowClick(null, row);
        }

        // Running the datum's keydown function
        if (row.keydown) {
            row.keydown(event, row);
        }
    }

    // Methods
    assembleData(data = this.data, initial = true, parent = null) {
        // POST: assembles the data for the virtual table recursively

        // On the first call, reset the data
        if (initial) {
            this.flatData = [];
        }

        // Applying the column header
        if (data.length > 0) {
            const ID = 'header' + (parent ? parent.ID : '');
            const headerData = new NestedData(null, parent, ID, data[0].identifier);
            headerData.header = true;
            this.flatData.push(headerData);
        }

        // For each data, push it to the flat data and push its children
        data.forEach(datum => {
            this.flatData.push(datum);
            if (datum.children) {
                this.assembleData(datum.children, false, datum);
            }
        });
    }

    filterShown() {
        // POST: returns the panelFiltered data that should be shown
        return this.flatData.filter(datum => datum.shown);
    }

    trackByID(index: number, item: NestedData) {
        // POST: tracks the items by their identifiers
        return item.ID;
    }

    createRange(indexes: number) {
        // POST: creates an array of numbers from 0 to indexes
        const arr = [];
        for (let i = 0; i < indexes; i++) {
            arr.push(i);
        }
        return arr;
    }
}
